import i18n from 'i18next';
import React from 'react';
import { getDate, getLocaleDateString } from 'src/Shared/Utilities/DateFormatter';
import {
  ALL_BANK_ACCOUNT,
  connector,
  getAllStaffTypeList,
  getDisbursementTypeList,
  getFeeEarnerOverviewSections,
  getFeeEarnerSummaryAttributeList,
  getFeeEarnerSummaryStaffTypeList,
  getFirmOverviewSections,
  getMatterBalanceTypeList,
  getMonthList,
  getPeriodList,
  getStaffTypeList,
  ReceiptBankedTypeEnum,
  UNDEPOSITED_FUNDS,
} from 'src/Shared/Utilities/constant';
import { getCurrentYearList, startOfMonth } from 'src/Shared/Utilities/dateDefaultValue';
import { createRequireValidator, ValidatorFn, ValidatorName } from 'src/Shared/filterValidators/validatorModel';
import { environment } from '../../environment';
import { ALL } from './ActivityCodeFilter';

const { t } = i18n;

const SingleSelect = React.lazy(() => import('./SingleSelector'));
const MultiSelect = React.lazy(() => import('./MultiSelector'));
const DatePicker = React.lazy(() => import('./DatePicker'));
const DateRange = React.lazy(() => import('./DateRangePicker'));
const CheckBox = React.lazy(() => import('./CheckboxFilter/WithDropdown'));
const Input = React.lazy(() => import('./InputFilter'));
const CheckBoxHidden = React.lazy(() => import('./CheckboxFilterHidden'));
const StaffListFilter = React.lazy(() => import('./StaffListFilter'));
const StaffForAttributedWorkTo = React.lazy(() => import('./StaffListFilter/StaffForAttributedWorkTo'));
const ActivityCodeFilter = React.lazy(() => import('./ActivityCodeFilter'));
const ExcludeAgedDaysFilter = React.lazy(() => import('./ExcludeAgedDaysFilter'));
const GroupByFilter = React.lazy(() => import('./GroupByFilter'));

export interface IStaff {
  StaffId: string;
  Name?: string;

  [key: string]: unknown;
}

export interface IComponentProps {
  filterName: Filter;
  isTopLevel?: boolean;
  passedInFilterModel?: IFilter;
  componentProps?: any;
  disabled?: boolean;
}
export interface IStaffWithActiveFlag extends IStaff {
  IsActive?: boolean;
  StaffName: string;
}

export interface ITrustBankAccount {
  BankAccId: string;
  BankAccountDesc: string;
}
export interface IAddressState {
  AddressState: string;
}

export interface IOfficeBankAccount {
  BankAccId: string;
  BankAccountDesc: string;
}

export interface IGeneralLedgerAccount {
  AccountCode: string;
  AccountTypeName: string;
}

export interface IGeneralLedgerTaxCode {
  TaxCodeName: string;
  TaxCode: string;
}

export interface IReportingGroup {
  ReportingGroupId: string;
  ReportingGroup: string;
}

export interface ICriticalDate {
  CriticalDateId: string;
  DisplayName: string;
}

export interface ISelectWithActiveFlag extends ISelectOption {
  isActive?: boolean;
}

export interface ITrustReconciliationDate {
  BankRecHistoryId: string;
  StatementDate: string;
  StatementDateString: string;
}

export type FilterName =
  | 'not defined'
  | 'AccountType'
  | 'ActivityFilter'
  | 'AddressedTo'
  | 'AtDateFilter'
  | 'AttributedWorkToFilter'
  | 'AttributeDebtorsFilter'
  | 'AttributeDisbursementsFilter'
  | 'AppointmentAssigneesFilter'
  | 'BillingCategoriesFilter'
  | 'BillingStatus'
  | 'CardTypeFilter'
  | 'CalculatedBy'
  | 'CalculatedByTransit'
  | 'CriticalDateFilter'
  | 'DateRangeFilter'
  | 'DateCommencementCurrent'
  | 'DetailItems'
  | 'DisbursementTransactionCategory'
  | 'DisplayDiscountOnInvoicesFilter'
  | 'DisplayPhoneNo'
  | 'DisplayReversals'
  | 'DisplaySummary'
  | 'DisplayTimeFees'
  | 'DisplayStaffInitials'
  | 'DisbursementTypeFilter'
  | 'DisplayTimeFeesFilter'
  | 'DocumentRegisterStatusFilter'
  | 'EndDateFilter'
  | 'EOMStatementYears'
  | 'ExcludeAgedDaysFilter'
  | 'ExpandRowGroupsFilter'
  | 'ExpandManuallyFilter'
  | 'ExpandFilter'
  | 'ExportStatusFilter'
  | 'FeeEarnerOverviewSectionsFilter'
  | 'FeeEarnerStaffTypeFilter'
  | 'FilterDate'
  | 'FirmOverviewSectionsFilter'
  | 'FeeTaskCodeFilter'
  | 'FromDateFilter'
  | 'GenericDropdownFilter'
  | 'GeneralLedgerAccounts'
  | 'GeneralLedgerTaxCodes'
  | 'GroupByFilter'
  | 'GroupByFilterInData'
  | 'HasMarketingConsent'
  | 'IncludeAddressFilter'
  | 'IncludeArchivedMattersFilter'
  | 'IncludeOtherSideFilter'
  | 'IncludeClientType'
  | 'IncludeClientAddress'
  | 'IncludeCreationDate'
  | 'IncludeDraftInvoicesFilter'
  | 'IncludePresentedItems'
  | 'IncludeTaxFilter'
  | 'IncludeInactiveUserFilter'
  | 'IncMattersWithNilBalancesFilter'
  | 'IncludeNonBillableFilter'
  | 'IncludeOnHoldFilter'
  | 'IncludeWIPOnHoldFilter'
  | 'IncludeInvoicesOnHold'
  | 'InvoicesFilter'
  | 'IsRapidPay'
  | 'ItemisedFilter'
  | 'LawConnectFilterBy'
  | 'MailToOption'
  | 'MatterStatusFilter'
  | 'MatterStatusForConveyancing'
  | 'MatterTaskAssigneesFilter'
  | 'MatterTasksDateTypeFilter'
  | 'MatterTypeFilter'
  | 'MatterTypeIdFilter'
  | 'MattersWithFilter'
  | 'MatterBalanceTypeFilter'
  | 'MatterBalanceTypeFilterForTrust'
  | 'MatterOptionFilter'
  | 'MatterListFilter'
  | 'NoActivityFor'
  | 'PaymentTypeFilter'
  | 'PrintOrderOption'
  | 'PrintOrderOptionPrebilling'
  | 'OtherDateFilterType'
  | 'OfficeAccountFilter'
  | 'OfficeAccountWithoutAll'
  | 'OutstandingInvoicesOnly'
  | 'PaidStatus'
  | 'PlanStatus'
  | 'Period'
  | 'Quarter'
  | 'MonthOrQuarterFilter'
  | 'ReportingGroupFilter'
  | 'SelectTopFilter'
  | 'ShowHeader'
  | 'ShowInvoicesWithFilter'
  | 'TransactionTypeFilter'
  | 'ToDateFilter'
  | 'RapidRefLookupFilter'
  | 'RegionFilter'
  | 'ReceiptsAtDateFilter'
  | 'StaffActingFilter'
  | 'StaffAssistingFilter'
  | 'StaffCreditFilter'
  | 'StaffListMultiSelectFilter'
  | 'StaffListSingleSelectFilter'
  | 'StaffListIdsSingleSelectFilter'
  | 'StaffForAttributedWorkTo'
  | 'StaffResponsibleFilter'
  | 'StaffTypeFilter'
  | 'StartDateFilter'
  | 'StatementDateFilter'
  | 'SupplierOnly'
  | 'SupplierPaid'
  | 'SummaryItems'
  | 'TimeFrameFilter'
  | 'TrustAccountFilter'
  | 'BankRecHistoryIdFilter'
  | 'TrustStatementActivity'
  | 'TrustTrialActivity'
  | 'TrustTrialOrderBy'
  | 'TransactionsFromFilter'
  | 'UndertakingGivenFilter'
  | 'UndertakingGivenAtDate'
  | 'UndertakingDischargeDate'
  | 'Month'
  | 'Year'
  | 'TrustYearFilter'
  | 'TrustGeneralYearFilter';

export interface ConfigurableFilter {
  filterName: FilterName;
  // filterOptions?: {
  //   validators?: ValidatorName[];
  //   // index number if transformDataToDataRequestFunctions is a function array instead of a function
  //   transformFunctionIndex?: number;
  //   staticDropDownOptionsList?: ISelectOption[];
  //   filterNamesInDataRequestBody?: string | string[];
  //   filterNamesInFileRequestBody?: string | string[];
  // };
  filterOptions?: Partial<IFilter>;
  defaultValue?: any;
}

export type Filter = ConfigurableFilter | FilterName;

export function getFilterNameString(filter: Filter): FilterName {
  let filterNameString: FilterName;
  if (typeof filter === 'string' || filter instanceof String) {
    filterNameString = filter as FilterName;
  } else if (typeof filter === 'object') {
    filterNameString = (filter as ConfigurableFilter).filterName;
  } else {
    filterNameString = filter as FilterName;
  }
  return filterNameString;
}

export interface ISelectOption {
  label: string;
  /**
   * used in frontend side
   */
  value: string | any;
  key?: string;
  isDefault?: boolean;
  /**
   * used in printing service
   */
  valueInReq?: string;
}

export interface IAssigneeOption {
  value: string | any;
  key?: string;
  isDefault?: boolean;
}

export interface IFilter {
  // Name must be unique
  name: FilterName;
  displayName: string;
  defaultValue?: any;
  component: React.JSXElementConstructor<any>;
  componentProps?: Record<string, any>;
  // check if filter should be included in the request when fetching json data
  // currently using filterNamesInDataRequestBody to determine, define this field for future use
  isInDataRequest?: boolean;

  // Filter emitted value can take multiple fields in the request body, eg.: generalLedgerAccountsFilter
  filterNamesInDataRequestBody?: any | any[];
  filterNamesInFileRequestBody?: any | any[];
  validators: (ValidatorName | ValidatorFn)[];
  // indicate if the value of the filter is valid
  isValid?: boolean;
  // first validation error info
  error?: string;
  //Get Grid API
  getGridApi?: any;
  // the output data from a filter
  data?: any;
  //pre process data from server ( deals with multiple filters data from one api call)
  preprocessFilterDataFromServer?: (arg: any) => any;
  // if data and filterNamesInDataRequestBody are not null, then transformDataToDataRequestFunctions can't be null
  // extra is used when filter's name in request body is dynamically set
  transformDataToDataRequestFunctions?: ((arg: any, ...extra: any) => any)[] | ((arg: any, ...extra: any) => any);
  // to pick the transformFunction if the above field is an array
  transformFunctionIndex?: number;
  transformDataToFileRequestFunctions?: ((arg: any, ...extra: any) => any)[] | ((arg: any, ...extra: any) => any);
  // api url to fetch filter data such as options in dropdown list
  fetchFilterDataAPI?: string | undefined;
  config?: {
    // this config is used to indicate if a filter's status will be modified
    // when include inactive user filter change, for example: staff filters.
    useIncInactive?: boolean;
    // indicate if the filter data is coming from API call or hardcoded.
    isStatic?: boolean;
  };
  // for a dropdown style filter, use this function to transform the remote data
  // to a ISelectOption type for Autocomplete component to use.
  dropDownOptionTransform?: (arg: any, index: number, arr: any[]) => ISelectWithActiveFlag;
  // if a filter is a static dropdown, use this to construct the filter's options list .
  staticDropDownOptionsList?: ISelectOption[];
  // filter's grouping options
  groupConfig?: {
    // group Name current filter belongs to
    groupName: string;
  };
  hidden?: boolean;
  disabled?: boolean;
  tooltip?: string;
  noOptionsPlaceholder?: string;
}

// When use this function to return a filter, watch out it'll return a new Object,if put in an dependency array of
// useEffect, it might cause dead loop render.
// UPDATE: memoize this function to return the same filter object between different renders.
// dead loop avoided when used as a dependency of useEffect
export const getFilterModelByName: (filter: Filter) => IFilter = (filter: Filter) => {
  const filterName = getFilterNameString(filter);
  return getCurrentFilterModel(filterName);
};

function getBaseFilterModel(filterName: FilterName): IFilter {
  switch (filterName) {
    case 'AccountType': {
      return {
        name: 'AccountType',
        displayName: t('Account'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'AccountType',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '0',
            label: t('Office'),
            isDefault: true,
          },
          {
            value: '1',
            label: t('Trust'),
          },
        ],
      };
    }

    case 'ActivityFilter': {
      return {
        name: 'ActivityFilter',
        displayName: t('Matters'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'Activity',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '0',
            label: t('With outstanding balances'),
            isDefault: true,
          },
          {
            value: '1',
            label: t('With balances and/or transactions'),
          },
        ],
      };
    }
    case 'AttributeDisbursementsFilter': {
      return {
        name: 'AttributeDisbursementsFilter',
        displayName: t('Attribute Disbursements'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'staffAttributionForDisbursements',

        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '1',
            label: t('Staff Responsible'),
            isDefault: true,
          },
          {
            value: '2',
            label: t('Staff Acting'),
          },
        ],
      };
    }
    case 'AddressedTo': {
      return {
        name: 'AddressedTo',
        displayName: t('For'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInFileRequestBody: 'AddressTo',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList:
          environment.region === 'uk'
            ? [
                {
                  value: 'ReceivedFrom',
                  label: t('Received From'),
                  isDefault: true,
                },
                {
                  value: 'Debtor',
                  label: t('Debtors'),
                },
              ]
            : [
                {
                  value: 'ReceivedFrom',
                  label: t('Received From'),
                  isDefault: true,
                },
                {
                  value: 'Debtor',
                  label: t('Debtors'),
                },
                {
                  value: 'SplitDebtor',
                  label: t('Debtors/Split Debtors'),
                },
              ],
      };
    }
    case 'AtDateFilter':
      return {
        name: 'AtDateFilter',
        displayName: t('At Date'),
        component: DatePicker,
        validators: ['validDate'],
        filterNamesInDataRequestBody: 'AtDate',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
      };
    case 'AttributedWorkToFilter': {
      return {
        name: 'AttributedWorkToFilter',
        displayName: t('Attribute Work To'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'WorkAttribution',

        transformDataToDataRequestFunctions: (i) => i.toString(),
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: getAllStaffTypeList(),
        groupConfig: {
          groupName: 'Attribute Work To',
        },
      };
    }
    case 'AppointmentAssigneesFilter': {
      return {
        name: 'AppointmentAssigneesFilter',
        displayName: t('Assignees'),
        filterNamesInDataRequestBody: 'Assignees',
        config: {
          isStatic: true,
        },
        component: React.lazy(() => import('./AppointmentAssigneesFilter')),
        validators: ['skip'],
        transformDataToDataRequestFunctions: (i) => i,
        dropDownOptionTransform: (value: IStaffWithActiveFlag) => {
          return {
            label: value.StaffName,
            value: value.StaffId,
            isActive: value?.IsActive,
          };
        },
      };
    }
    case 'BillingCategoriesFilter': {
      return {
        name: 'BillingCategoriesFilter',
        displayName: t('Billing Category'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'BillingCategoryIds',
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/filter/billingcategories`,

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        dropDownOptionTransform: (value: { BillingCategory: string; BillingCategoryId: string }) => {
          return {
            label: value.BillingCategory,
            value: value.BillingCategoryId,
          };
        },
      };
    }

    case 'BillingStatus': {
      return {
        name: 'BillingStatus',
        displayName: t('Billing Type'),
        component: MultiSelect,
        filterNamesInDataRequestBody: 'BillingStatus',
        validators: ['atLeastOneSelection'],

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '0',
            label: t('Billable'),
          },
          {
            value: '1',
            label: t('Not Billable'),
          },
          {
            value: '2',
            label: t('Billed'),
          },
        ],
      };
    }

    case 'CardTypeFilter': {
      return {
        name: 'CardTypeFilter',
        displayName: t('Card Type'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'CardType',

        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'People',
            label: t('People'),
          },
          { value: 'Business', label: t('Business') },
          { value: 'Company', label: t('Company') },
          { value: 'Government', label: t('Government') },
          { value: 'Trust', label: t('Trust') },
        ],
      };
    }
    case 'CalculatedBy': {
      return {
        name: 'CalculatedBy',
        component: SingleSelect,
        validators: ['required'],
        displayName: t('Run By'),
        filterNamesInDataRequestBody: 'SortBy',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'TransactionDate',
            label: t('Transaction Date'),
            isDefault: true,
          },
          {
            value: 'EntryDate',
            label: t('Entry Date'),
          },
        ],
      };
    }
    case 'CalculatedByTransit': {
      return {
        name: 'CalculatedByTransit',
        displayName: t('Calculated By'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'SortBy',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'All',
            label: t('All'),
            isDefault: true,
          },
          {
            value: 'ReceivedDate',
            label: t('Received Date'),
          },
          {
            value: 'PaidDate',
            label: t('Paid Date'),
          },
        ],
      };
    }
    case 'CriticalDateFilter':
      return {
        name: 'CriticalDateFilter',
        displayName: t('Critical Date'),
        validators: [
          (data: any[]) => {
            return {
              isValid: data?.length > 0,
              error: t('No critical dates selected'),
            };
          },
        ],
        noOptionsPlaceholder: t('No critical dates found in date range'),
        filterNamesInDataRequestBody: 'CriticalDateIds',
        isValid: true,
        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [],
        component: React.lazy(() => import('./CriticalDatesFilter')),
      };
    case 'DateRangeFilter':
      return {
        name: 'DateRangeFilter',
        displayName: t('Date Range'),
        component: DateRange,
        validators: ['validDateRange'],
        filterNamesInDataRequestBody: 'DateRange',

        transformDataToDataRequestFunctions: (i: any) => i,
      };

    case 'DateCommencementCurrent':
      return {
        name: 'DateCommencementCurrent',
        component: CheckBox,
        validators: ['required'],
        displayName: t('Current Power Commencement Date'),
        filterNamesInDataRequestBody: 'IsCurrentPowerCommencementDate',

        transformDataToDataRequestFunctions: (i: any) => i,
      };

    case 'DetailItems': {
      return {
        name: 'DetailItems',
        displayName: t('Detail Items'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'Detail',

        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'UnbilledWip',
            label: t('Unbilled Fees'),
          },
          {
            value: 'BillableLaterWIP',
            label: t('Billable Later Fees'),
          },
          {
            value: 'NotBillableTimeFees',
            label: t('Not Billable Fees'),
          },
          {
            value: 'UnbilledPayments',
            label: t('Unbilled Payments'),
          },
          {
            value: 'UnbilledCR',
            label: t('Unbilled Cost Recoveries'),
          },
          {
            value: 'UnbilledAntPayments',
            label: t('Unbilled Anticipated Payments'),
          },
          {
            value: 'UnbilledJournals',
            label: t('Unbilled Journal Debtors'),
          },
        ],
      };
    }
    case 'DisbursementTransactionCategory': {
      return {
        name: 'DisbursementTransactionCategory',
        displayName: t('Status'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'DisbursementTransactionCategory',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'All',
            label: t('All'),
            isDefault: true,
          },
          {
            value: 'Billable',
            label: t('Billable'),
          },
          {
            value: 'NonBillable',
            label: t('Non-Billable'),
          },
          {
            value: 'Billed',
            label: t('Billed'),
          },
          {
            value: 'ReversedOrDeleted',
            label: t('Reversed/Deleted'),
          },
        ],
      };
    }
    case 'DisplayDiscountOnInvoicesFilter':
      return {
        name: 'DisplayDiscountOnInvoicesFilter',
        displayName: t('Discounted/Adjusted Invoices Only'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInDataRequestBody: 'Discount',
        transformDataToDataRequestFunctions: [(i: any) => Number(i), (i) => i],
      };
    case 'DisbursementTypeFilter':
      return {
        name: 'DisbursementTypeFilter',
        displayName: t('Disbursement Type'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'DisbursementType',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: getDisbursementTypeList(),
      };
    case 'DisplayPhoneNo':
      return {
        name: 'DisplayPhoneNo',
        displayName: t('Debtor/Client Phone Number'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInFileRequestBody: 'displayClientPhone',
        transformDataToFileRequestFunctions: (v) => v,
        groupConfig: {
          groupName: 'Display',
        },
      };

    case 'DisplaySummary':
      return {
        name: 'DisplaySummary',
        displayName: t('Display Summary'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInDataRequestBody: 'DisplaySummary',
        transformDataToDataRequestFunctions: (i: any) => i,
        groupConfig: {
          groupName: 'Display',
        },
      };

    case 'DisplayReversals':
      return {
        name: 'DisplayReversals',
        displayName: t('Include reversals of invoices transacted outside of date range'),
        filterNamesInDataRequestBody: 'IncludeReversalWithoutInvoice',
        component: CheckBox,
        validators: ['required'],
        //
        // transformDataToDataRequestFunctions: (i: any) => i.toString(),
        transformDataToDataRequestFunctions: (i: any) => i,
        groupConfig: {
          groupName: 'Display',
        },
      };
    case 'DisplayStaffInitials':
      return {
        name: 'DisplayStaffInitials',
        displayName: t('Display Staff Initials for'),
        component: SingleSelect,
        validators: ['required'],

        filterNamesInFileRequestBody: 'displayInitialsFor',
        transformDataToFileRequestFunctions: [(i: any) => i.toString(), (i) => i],
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          { value: 'None', label: t('None') },
          {
            value: 'StaffResponsible',
            label: t('Staff Responsible'),
            isDefault: true,
          },
          { value: 'StaffActing', label: t('Staff Acting') },
          { value: 'StaffAssisting', label: t('Staff Assisting') },
          {
            value: 'StaffCredit',
            label: t('Staff Credit'),
          },
        ],
        groupConfig: {
          groupName: 'Display',
        },
      };

    case 'DisplayTimeFeesFilter':
      return {
        name: 'DisplayTimeFeesFilter',
        displayName: t('Transaction'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'Type',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          { value: 'TimeEntered', label: t('Time Entered Hours') },
          {
            value: 'Invoiced',
            label: t('Time and Fees Invoiced'),
            isDefault: true,
          },
          { value: 'Receipted', label: t('Time and Fees Received') },
        ],
        groupConfig: {
          groupName: 'Display',
        },
      };
    case 'DocumentRegisterStatusFilter': {
      return {
        name: 'DocumentRegisterStatusFilter',
        displayName: t('Status'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'Status',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: { isStatic: true },
        staticDropDownOptionsList: [
          {
            value: '200',
            label: t('All'),
            isDefault: true,
          },
          {
            value: '100',
            label: t('Currently Held /Temporarily Removed'),
          },
          {
            value: '0',
            label: t('Currently Held'),
          },
          {
            value: '1',
            label: t('Temporarily Removed'),
          },
          {
            value: '-1',
            label: t('Permanently Removed'),
          },
          {
            value: '-2',
            label: t('Deleted'),
          },
        ],
      };
    }
    case 'EndDateFilter':
      return {
        name: 'EndDateFilter',
        displayName: t('End Date'),
        component: DatePicker,
        validators: ['validDate'],
        filterNamesInDataRequestBody: 'EndDate',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
      };

    case 'EOMStatementYears': {
      return {
        name: 'EOMStatementYears',
        displayName: t('Statements Of'),
        component: SingleSelect,
        validators: ['required'],
        // filterNamesInDataRequestBody: 'years',
        // transformDataToDataRequestFunctions: i => i.toString(),
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '0',
            label: t('Last 1 Year'),
          },
          {
            value: '1',
            label: t('Last 2 Years'),
          },
          {
            value: '2',
            label: t('All Years'),
            isDefault: true,
          },
        ],
      };
    }
    case 'ExcludeAgedDaysFilter':
      return {
        name: 'ExcludeAgedDaysFilter',
        displayName: t('Exclude Aged Days'),
        component: ExcludeAgedDaysFilter,
        isValid: true,
        validators: ['skip'],
        componentProps: {
          placeholder: '0',
        },
        filterNamesInDataRequestBody: 'AgedDaysToExclude',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        transformDataToFileRequestFunctions: (i) => +i,
      };
    case 'ExpandRowGroupsFilter':
      return {
        name: 'ExpandRowGroupsFilter',
        displayName: t('Expand Row Groups'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInFileRequestBody: 'expand',
        transformDataToFileRequestFunctions: (i) => i,
        // transformDataToDataRequestFunctions: (i: any) => i.toString(),
        groupConfig: {
          groupName: 'Display',
        },
      };
    case 'ExpandManuallyFilter':
      return {
        name: 'ExpandManuallyFilter',
        displayName: t('Expand Row Groups'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInFileRequestBody: 'expand',
        transformDataToFileRequestFunctions: (i) => i,
        // transformDataToDataRequestFunctions: (i: any) => i.toString(),
        groupConfig: {
          groupName: 'Display',
        },
      };
    case 'ExpandFilter':
      return {
        name: 'ExpandFilter',
        displayName: t('Expand'),
        config: { isStatic: true },
        component: SingleSelect,
        validators: ['required'],
        filterNamesInFileRequestBody: 'expand',
        transformDataToFileRequestFunctions: (i) => i,
        staticDropDownOptionsList: [],
        groupConfig: {
          groupName: 'Display',
        },
      };
    case 'ExportStatusFilter': {
      return {
        name: 'ExportStatusFilter',
        displayName: t('Export Status (Details Only)'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'StatusId',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: { isStatic: true },
        staticDropDownOptionsList: [
          {
            value: '1',
            label: t('Exported'),
          },
          {
            value: '2',
            label: t('Hold'),
          },
          {
            value: '3',
            label: t('All'),
            isDefault: true,
          },
        ],
      };
    }
    case 'FeeEarnerOverviewSectionsFilter':
      return {
        name: 'FeeEarnerOverviewSectionsFilter',
        displayName: t('Sections'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'Sections',

        //Array of values [0, 1]
        transformDataToDataRequestFunctions: (i: any) => i,
        config: { isStatic: true },
        staticDropDownOptionsList: getFeeEarnerOverviewSections(),
      };
    case 'FilterDate':
      return {
        name: 'FilterDate',
        displayName: t('Filter Date'),
        component: DatePicker,
        validators: ['skip'],
        filterNamesInDataRequestBody: 'OtherFilterDate',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
      };
    case 'FirmOverviewSectionsFilter':
      return {
        name: 'FirmOverviewSectionsFilter',
        displayName: t('Sections'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'Sections',

        //Array of values [0, 1]
        transformDataToDataRequestFunctions: (i: any) => i,
        config: { isStatic: true },
        staticDropDownOptionsList: getFirmOverviewSections(),
      };
    case 'FeeTaskCodeFilter':
      return {
        name: 'FeeTaskCodeFilter',
        displayName: t('Activity Code'),
        component: ActivityCodeFilter,
        validators: ['required'],
        filterNamesInDataRequestBody: 'TaskCodeIds',

        transformDataToDataRequestFunctions: (i) => {
          if (i === ALL) {
            return [];
          } else {
            return [i];
          }
        },
      };

    case 'FromDateFilter':
      return {
        name: 'FromDateFilter',
        displayName: t('Start Date'),
        component: DatePicker,
        validators: ['validDate'],
        filterNamesInDataRequestBody: 'From',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
      };

    case 'GeneralLedgerAccounts': {
      return {
        name: 'GeneralLedgerAccounts',
        displayName: t('Accounts'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: ['AccountTypeNames', 'AccountCodes'],
        transformDataToDataRequestFunctions: [
          (
            i: {
              AccountTypeName: string;
              AccountCode: string;
            }[],
            { filterName },
          ) => {
            if (filterName === 'AccountTypeNames') {
              return i?.map((j) => j?.AccountTypeName)?.toString();
            }
            return i?.map((j) => j?.AccountCode)?.toString();
          },
          (
            i: {
              AccountTypeName: string;
              AccountCode: string;
            }[],
            { filterName },
          ) => {
            if (filterName === 'AccountTypeNames') {
              return i?.map((j) => j?.AccountTypeName);
            }
            return i?.map((j) => j?.AccountCode);
          },
        ],
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/generalledgeraccounts`,
        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: IGeneralLedgerAccount) => {
          return {
            label: value?.AccountTypeName,
            value: value,
          };
        },
      };
    }
    case 'GeneralLedgerTaxCodes': {
      return {
        name: 'GeneralLedgerTaxCodes',
        displayName: t('Tax Codes'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: ['TaxCodes'],

        transformDataToDataRequestFunctions: (i) => i.map((item) => item.split(connector)[1]),
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/generalledgertaxcodes`,
        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: IGeneralLedgerTaxCode, index: number) => {
          return {
            label: value?.TaxCodeName,
            value: `${index}_${value?.TaxCodeName}${connector}${value?.TaxCode}`,
          };
        },
      };
    }
    case 'GroupByFilter':
      return {
        name: 'GroupByFilter',
        displayName: t('Group By'),
        validators: ['required'],
        component: GroupByFilter,
        filterNamesInFileRequestBody: 'groupBy',
        transformDataToFileRequestFunctions: (i) => i?.valueInReq,
        staticDropDownOptionsList: [],
        groupConfig: {
          groupName: 'Display',
        },
      };
    case 'GroupByFilterInData':
      return {
        name: 'GroupByFilterInData',
        displayName: t('Group By'),
        component: GroupByFilter,
        validators: ['required'],

        filterNamesInDataRequestBody: 'groupBy',
        transformDataToDataRequestFunctions: (i) => i?.valueInReq,
        staticDropDownOptionsList: [],
        groupConfig: {
          groupName: 'Display',
        },
      };

    case 'HasMarketingConsent':
      return {
        name: 'HasMarketingConsent',
        displayName: t('Has Marketing Consent'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInDataRequestBody: 'HasMarketingConsentOnly',

        transformDataToDataRequestFunctions: (i) => i,
      };

    case 'IncludeClientType': {
      return {
        name: 'IncludeClientType',
        component: SingleSelect,
        displayName: t('For'),
        validators: ['required'],
        filterNamesInFileRequestBody: 'AddressToDebtorOnly',
        transformDataToFileRequestFunctions: [(i) => i === 'true'],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'false',
            label: t('All Clients'),
            isDefault: true,
          },
          {
            value: 'true',
            label: t('Debtors Only'),
          },
        ],
      };
    }

    case 'IncludeClientAddress': {
      return {
        name: 'IncludeClientAddress',
        displayName: t('Include Client Address'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInDataRequestBody: 'IncludeClientAddress',

        transformDataToDataRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Include',
        },
      };
    }

    case 'IncludeCreationDate':
      return {
        name: 'IncludeCreationDate',
        displayName: t('Add Creation Date Filter'),
        component: CheckBox,
        defaultValue: 'true',
        validators: ['required'],
        filterNamesInDataRequestBody: 'IncludeCreationDate',

        transformDataToDataRequestFunctions: (i) => i,
      };
    case 'IncludePresentedItems':
      return {
        name: 'IncludePresentedItems',
        displayName: t('Include Presented Items'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInDataRequestBody: 'IncludePresentedItems',

        transformDataToDataRequestFunctions: (i: any) => i,
        groupConfig: {
          groupName: 'Include',
        },
      };
    case 'IncludeTaxFilter':
      return {
        name: 'IncludeTaxFilter',
        displayName: t('Include Tax'),
        component: CheckBox,
        validators: ['required'],
        defaultValue: 'true',
        filterNamesInFileRequestBody: 'IncTax',

        transformDataToFileRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Include',
        },
      };
    case 'IncludeAddressFilter':
      return {
        name: 'IncludeAddressFilter',
        displayName: t('Include Address'),
        validators: ['required'],
        component: CheckBox,
        filterNamesInDataRequestBody: 'IncludeAddress',

        transformDataToDataRequestFunctions: (i: any) => i,
        groupConfig: {
          groupName: 'Include',
        },
      };
    case 'IncludeArchivedMattersFilter':
      return {
        name: 'IncludeArchivedMattersFilter',
        displayName: t('Include Archived Matters'),
        validators: ['required'],
        component: CheckBox,
        filterNamesInDataRequestBody: 'incArchivedMatters',

        transformDataToDataRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Include',
        },
      };
    case 'IncludeOtherSideFilter':
      return {
        name: 'IncludeOtherSideFilter',
        displayName: t('Include Other Side'),
        validators: ['required'],
        component: CheckBox,
        filterNamesInDataRequestBody: 'IncludeOtherSide',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        transformDataToFileRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Include',
        },
      };
    case 'IncludeDraftInvoicesFilter':
      return {
        name: 'IncludeDraftInvoicesFilter',
        displayName: t('Include Draft Invoices'),
        filterNamesInDataRequestBody: 'IncDraft',
        validators: ['required'],
        component: CheckBox,

        transformDataToDataRequestFunctions: (i) => i,
        transformDataToFileRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Include',
        },
      };
    case 'IncludeInvoicesOnHold':
      return {
        name: 'IncludeInvoicesOnHold',
        displayName: t('Include Billable - Later Invoice Items'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInDataRequestBody: 'IncludeInvoicesOnHold',

        transformDataToDataRequestFunctions: (i: any) => i,
        groupConfig: {
          groupName: 'Include',
        },
      };
    case 'IncludeInactiveUserFilter':
      return {
        name: 'IncludeInactiveUserFilter',
        component: CheckBox,
        validators: ['required'],
        displayName: t('Include Inactive Staff'),
        groupConfig: {
          groupName: 'Staff',
        },
      };
    case 'IncMattersWithNilBalancesFilter':
      return {
        name: 'IncMattersWithNilBalancesFilter',
        displayName: t('Include Matters With Nil Balances'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInDataRequestBody: 'IncMattersWithNilBalances',
        transformDataToDataRequestFunctions: (i: any) => i,
        groupConfig: {
          groupName: 'Include',
        },
      };
    case 'IncludeNonBillableFilter':
      return {
        name: 'IncludeNonBillableFilter',
        displayName: t('Non Billable'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInDataRequestBody: 'IncNonBillable',

        transformDataToDataRequestFunctions: (i) => i,
        transformDataToFileRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Include',
        },
      };
    case 'IncludeOnHoldFilter':
      const path = window.location.pathname;
      //Aged Disbursement and Aged WIP and Disbursement Endpoints haven't been updated to be a truth value, therefore this filter converts to integer
      const checkPath = (i: any) => {
        if (path === '/officeMenu/officeAgedPayments' || path === '/officeMenu/agedUnbilledReport') {
          return i.toString() === 'false' ? 0 : 1;
        } else {
          return i.toString();
        }
      };

      return {
        name: 'IncludeOnHoldFilter',
        displayName: t('Include Billable - Later Invoice Items'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInDataRequestBody: 'IncludeItemsOnHold',
        transformDataToDataRequestFunctions: (i: any) => checkPath(i),
        groupConfig: {
          groupName: 'Include',
        },
      };

    case 'IncludeWIPOnHoldFilter':
      return {
        name: 'IncludeWIPOnHoldFilter',
        displayName: t('Include Billable - Later Invoice WIP'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInDataRequestBody: 'IncHold',

        transformDataToDataRequestFunctions: (i) => i,
        transformDataToFileRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Include',
        },
      };
    case 'InvoicesFilter':
      return {
        name: 'InvoicesFilter',
        displayName: t('Invoice Status'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'InvoiceStatus',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          { value: '0', label: t('Draft Unapproved') },
          { value: '1', label: t('Draft Approved') },
          { value: '3', label: t('Printed Sent (Final)') },
        ],
      };
    case 'IsRapidPay':
      return {
        name: 'IsRapidPay',
        displayName: '',
        component: CheckBoxHidden,
        validators: ['required'],
        filterNamesInDataRequestBody: 'IsRapidPay',

        transformDataToDataRequestFunctions: (i) => i,
      };

    case 'ItemisedFilter':
      return {
        name: 'ItemisedFilter',
        displayName: t('Itemised'),
        component: CheckBox,
        defaultValue: 'true',
        filterNamesInFileRequestBody: 'isItemised',
        transformDataToFileRequestFunctions: (i) => i,
        validators: ['required'],
      };

    case 'LawConnectFilterBy':
      return {
        name: 'LawConnectFilterBy',
        component: SingleSelect,
        validators: ['required'],
        displayName: t('By'),
        filterNamesInFileRequestBody: 'type',

        transformDataToDataRequestFunctions: (i: any) => i.toString(),
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'byfirm',
            label: t('Firm'),
            isDefault: true,
          },
          {
            value: 'Staff',
            label: t('Staff'),
          },
          {
            value: 'bymatter',
            label: t('Matter'),
          },
        ],
      };

    case 'MailToOption': {
      return {
        name: 'MailToOption',
        displayName: t('For'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'MailToType',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList:
          environment.region === 'uk'
            ? [
                {
                  value: 'Client',
                  label: t('All Clients'),
                  isDefault: true,
                },
                {
                  value: 'Debtor',
                  label: t('Debtors'),
                },
              ]
            : [
                {
                  value: 'Client',
                  label: t('All Clients'),
                  isDefault: true,
                },
                {
                  value: 'Debtor',
                  label: t('Debtors Only'),
                },
                {
                  value: 'SplitDebtor',
                  label: t('Debtors/Split Debtors'),
                },
              ],
      };
    }

    case 'MatterStatusFilter': {
      return {
        name: 'MatterStatusFilter',
        displayName: t('Matter Status'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'MatterStatus',

        transformDataToDataRequestFunctions: (i) => i,
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/filter/matterstatuslist`,
        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: { MatterStatus: string }) => {
          return {
            label: value.MatterStatus,
            value: value.MatterStatus,
          };
        },
        groupConfig: {
          groupName: 'Matters',
        },
      };
    }
    case 'MatterStatusForConveyancing': {
      return {
        name: 'MatterStatusForConveyancing',
        displayName: t('Matter Status'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'MatterStatus',
        transformDataToDataRequestFunctions: (i) => i,
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/filter/matterstatusandtypesforconveyancingmatters`,
        preprocessFilterDataFromServer: (value: { MatterStatuses: any[] }) => value.MatterStatuses,
        dropDownOptionTransform: (value: { MatterStatus: string }) => {
          return {
            label: value.MatterStatus,
            value: value.MatterStatus,
          };
        },
        groupConfig: {
          groupName: 'Matters',
        },
      };
    }

    case 'MatterTaskAssigneesFilter': {
      return {
        name: 'MatterTaskAssigneesFilter',
        displayName: t('Assignees'),
        filterNamesInDataRequestBody: 'Assignees',
        component: React.lazy(() => import('./MatterTaskAssigneesFilter')),
        validators: ['skip'],
        transformDataToDataRequestFunctions: (i) => i,
        dropDownOptionTransform: (value: IStaffWithActiveFlag) => {
          return {
            label: value.StaffName,
            value: value.StaffId,
            isActive: value?.IsActive,
          };
        },
        fetchFilterDataAPI: '',
      };
    }

    case 'MatterTasksDateTypeFilter': {
      return {
        name: 'MatterTasksDateTypeFilter',
        displayName: t('Date Filter'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'DateFilter',

        transformDataToDataRequestFunctions: (i: any) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '0',
            label: t('None'),
          },
          {
            value: '1',
            label: t('Task Start Date'),
            isDefault: true,
          },
          {
            value: '2',
            label: t('Task Due Date'),
          },
        ],
      };
    }
    case 'MatterTypeFilter': {
      return {
        name: 'MatterTypeFilter',
        displayName: t('Matter Type'),
        filterNamesInDataRequestBody: 'MatterType',
        component: MultiSelect,
        validators: ['atLeastOneSelection'],

        transformDataToDataRequestFunctions:  (i) => i,
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/filter/mattertypelist`,
        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: { MatterTypeName: string; MatterTypeId: string }) => {
          return {
            label: value.MatterTypeName,
            value: value.MatterTypeId,
          };
        },
        groupConfig: {
          groupName: 'Matters',
        },
      };
    }
    case 'MatterTypeIdFilter': {
      return {
        name: 'MatterTypeIdFilter',
        displayName: t('Matter Type'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'MatterTypeIds',

        transformDataToDataRequestFunctions: (i) => i,
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/filter/mattertypelist`,
        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: { MatterTypeName: string; MatterTypeId: string }) => {
          return {
            label: value.MatterTypeName,
            value: value.MatterTypeId,
          };
        },
        groupConfig: {
          groupName: 'Matters',
        },
      };
    }

    case 'MatterBalanceTypeFilter': {
      return {
        name: 'MatterBalanceTypeFilter',
        displayName: t('Matters'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'MatterBalanceType',
        filterNamesInFileRequestBody: 'OnlyOutstandingInvoices',

        transformDataToDataRequestFunctions: (i) => i,
        transformDataToFileRequestFunctions: (i) => {
          return i === getMatterBalanceTypeList()[0].value;
        },
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: getMatterBalanceTypeList(),
      };
    }
    case 'MatterBalanceTypeFilterForTrust': {
      return {
        name: 'MatterBalanceTypeFilterForTrust',
        displayName: t('Matters'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'MatterBalanceType',
        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'MattersWithOutstandingBalances',
            label: t('With balances only'),
            isDefault: true,
          },
          {
            value: 'MattersWithBalancesAndTransactions',
            label: t('With transactions'),
          },
        ],
      };
    }
    case 'MattersWithFilter': {
      return {
        name: 'MattersWithFilter',
        displayName: t('Matter'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'MatterOption',

        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '0',
            label: t('Current matters'),
          },
          {
            value: '1',
            label: t('Current matters with unbilled balance'),
            isDefault: true,
          },
          {
            value: '2',
            label: t('Current matters unbilled balance by Staff Acting'),
          },
          {
            value: 3,
            label: t('Current matters unbilled balance by Staff Responsible'),
          },
          {
            value: 4,
            label: t('Current matters unbilled balance by Staff Assisting'),
          },
          {
            value: 5,
            label: t('Current matters unbilled balance by Staff Credit'),
          },
        ],
      };
    }
    case 'MatterOptionFilter': {
      return {
        name: 'MatterOptionFilter',
        displayName: t('Archived Status'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'MatterOption',

        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'NonArchived',
            label: t('Currently Non Archived Matters'),
            isDefault: true,
          },
          {
            value: 'Archived',
            label: t('Currently Archived Matters'),
          },
          {
            value: 'All',
            label: t('All Matters'),
          },
        ],
      };
    }
    case 'MatterListFilter':
      return {
        name: 'MatterListFilter',
        displayName: t('Matters'),
        component: React.lazy(() => import('./MatterListFilter')),
        validators: ['skip'],
        isValid: true,
        filterNamesInDataRequestBody: 'MatterIds',
        transformDataToDataRequestFunctions: (i) => i,
      };

    case 'NoActivityFor': {
      return {
        name: 'NoActivityFor',
        displayName: t('No Activity For'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'DormantMonths',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '1',
            label: t('1 Month'),
          },
          {
            value: '2',
            label: t('2 Months'),
          },
          {
            value: '3',
            label: t('3 Months'),
            isDefault: true,
          },
          {
            value: '6',
            label: t('6 Months'),
          },
          {
            value: '12',
            label: t('12 Months'),
          },
          {
            value: '24',
            label: t('24 Months'),
          },
        ],
      };
    }

    case 'OtherDateFilterType': {
      return {
        name: 'OtherDateFilterType',
        displayName: t('Other Date Filter'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'OtherDateFilterType',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'None',
            label: t('None'),
            isDefault: true,
          },
          {
            value: 'AMLCheckDate',
            label: t('AML Check Date'),
          },
          {
            value: 'ConflictCheckDate',
            label: t('Conflict Check Date'),
          },
          {
            value: 'IdentityCheckDate',
            label: t('Identity Check Date'),
          },
          {
            value: 'RetentionReviewDate',
            label: t('Retention Check Date'),
          },
          {
            value: 'ReviewDate',
            label: t('Review Date'),
          },
          {
            value: 'RiskAssessmentCompleteDate',
            label: t('Risk Assessment Complete Date'),
          },
          {
            value: 'SourceOfFundsDate',
            label: t('Source Of Funds Date'),
          },
          {
            value: 'UndertakingReceivedDate',
            label: t('Undertaking Received Date'),
          },
        ],
      };
    }

    case 'PlanStatus': {
      return {
        name: 'PlanStatus',
        displayName: t('Plan Status'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'PlanStatus',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '2',
            label: t('Active'),
            isDefault: true,
          },
          {
            value: '1',
            label: t('Pending'),
          },
          {
            value: '3',
            label: t('Cancelled'),
          },
          {
            value: '4',
            label: t('Expired'),
          },
          {
            value: '5',
            label: t('Complete'),
          },
        ],
      };
    }
    case 'Period': {
      return {
        name: 'Period',
        displayName: t('Period'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'Period',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: getPeriodList(),
      };
    }
    case 'OfficeAccountFilter': {
      return {
        name: 'OfficeAccountFilter',
        displayName: t('Bank Account'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: ['BankAccountId', 'ReceiptType'],
        transformDataToDataRequestFunctions: (i, { filterName }) => {
          if (filterName === 'ReceiptType') {
            const targetAccountIndex = [ALL_BANK_ACCOUNT, UNDEPOSITED_FUNDS].findIndex((item) => item.value === i);
            return targetAccountIndex >= 0 ? ReceiptBankedTypeEnum[targetAccountIndex] : ReceiptBankedTypeEnum[2];
          } else {
            return [ALL_BANK_ACCOUNT, UNDEPOSITED_FUNDS].find((item) => item.value === i) ? null : i;
          }
        },
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/filter/customofficebankaccounts `,
        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: IOfficeBankAccount) => {
          return {
            label: value?.BankAccountDesc,
            value: value?.BankAccId,
          };
        },
      };
    }

    case 'OfficeAccountWithoutAll': {
      return {
        name: 'OfficeAccountWithoutAll',
        displayName: t('Bank Account'),
        filterNamesInDataRequestBody: 'BankAccountId',
        component: SingleSelect,
        validators: ['required'],

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        // TODO: replace it with new endpoint
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/filter/officebankaccounts`,

        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: IOfficeBankAccount) => {
          return {
            label: value?.BankAccountDesc,
            value: value?.BankAccId,
          };
        },
      };
    }
    case 'OutstandingInvoicesOnly':
      return {
        name: 'OutstandingInvoicesOnly',
        displayName: t('Outstanding Invoices Only'),
        filterNamesInDataRequestBody: 'Outstanding',
        component: CheckBox,
        validators: ['required'],

        transformDataToDataRequestFunctions: [(i: any) => Number(i), (i) => i],
      };
    case 'PaidStatus':
      return {
        name: 'PaidStatus',
        displayName: t('Paid Status'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'Status',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'All',
            label: t('All'),
            isDefault: true,
          },
          {
            value: 'Paid',
            label: t('Paid'),
          },
          {
            value: 'Unpaid',
            label: t('Unpaid'),
          },
          {
            value: 'Deleted',
            label: t('Deleted'),
          },
        ],
      };

    case 'PaymentTypeFilter': {
      //Array of string values
      return {
        name: 'PaymentTypeFilter',
        displayName: t('Payment Type'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'PaymentTypes',

        transformDataToDataRequestFunctions: (i: any) => i,
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/filter/paymenttypes`,
        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: { PaymentTypeName: string }) => {
          return {
            label: value.PaymentTypeName,
            value: value.PaymentTypeName,
          };
        },
      };
    }
    case 'PrintOrderOption': {
      return {
        name: 'PrintOrderOption',
        displayName: t('Order Invoices By'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'orderBy',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'InvoiceNo',
            label: t('Invoice No.'),
            isDefault: true,
          },
          {
            value: 'MatterNo',
            label: t('Matter No.'),
          },
        ],
      };
    }

    case 'PrintOrderOptionPrebilling': {
      return {
        name: 'PrintOrderOptionPrebilling',
        displayName: t('Order By'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInFileRequestBody: 'orderBy',

        transformDataToFileRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'MatterNo',
            label: t('Matter No.'),
            isDefault: true,
          },
          {
            value: 'Debtor',
            label: t('Debtor'),
          },
          {
            value: 'StaffResponsible',
            label: t('Staff Responsible'),
          },
          {
            value: 'StaffActing',
            label: t('Staff Acting'),
          },
          {
            value: 'StaffAssisting',
            label: t('Staff Assisting'),
          },
          {
            value: 'StaffCredit',
            label: t('Staff Credit'),
          },
        ],
      };
    }
    case 'Quarter': {
      return {
        name: 'Quarter',
        displayName: t('Quarter'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'Quarter',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/financialyear`,
        preprocessFilterDataFromServer: (data) => {
          const { FinancialYearStartMonth } = data;
          const date = getDate();
          const monthOfToday = date.month();
          date.set('month', FinancialYearStartMonth - 1);
          date.set('date', 1);
          const result = new Array(4).fill(undefined);
          return result.map((item, index) => {
            const currentMonth = date.month();
            const startDate = getLocaleDateString(date, { month: 'long' });
            date.set('month', (currentMonth + 2) % 12);
            const endDate = getLocaleDateString(date, { month: 'long' });
            // set date for next loop
            date.set('month', (currentMonth + 3) % 12);
            return {
              value: index + 1,
              label: `Q${index + 1} (${startDate}-${endDate})`,
              isDefault: monthOfToday >= currentMonth && monthOfToday <= currentMonth + 3,
            };
          });
        },
        dropDownOptionTransform: (item) => item,
      };
    }
    case 'Month': {
      return {
        name: 'Month',
        displayName: t('Month'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'Month',
        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: getMonthList('long'),
      };
    }
    case 'Year': {
      return {
        name: 'Year',
        displayName: t('Year'),
        filterNamesInDataRequestBody: 'Year',
        component: SingleSelect,
        validators: ['required'],
        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: getCurrentYearList(),
      };
    }
    case 'RapidRefLookupFilter':
      return {
        name: 'RapidRefLookupFilter',
        displayName: t('Reference Number'),
        component: Input,
        componentProps: {
          maxLength: 12,
          placeholder: 'Input reference number here',
          input: { style: { width: 200 } },
        },
        filterNamesInDataRequestBody: 'btiReferenceNumber',
        validators: [
          (data: string | undefined) => {
            return {
              isValid: `${data ?? ''}`.length === 12,
              error: t('The reference number is invalid'),
            };
          },
        ],
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
      };

    case 'ReportingGroupFilter':
      return {
        name: 'ReportingGroupFilter',
        displayName: t('Reporting Group'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'ReportingGroups',

        transformDataToDataRequestFunctions: (i) => i,
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/filter/reportinggroups`,
        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: IReportingGroup) => {
          return {
            label: value.ReportingGroup,
            value: value.ReportingGroupId,
          };
        },
        groupConfig: {
          groupName: 'Matters',
        },
      };
    case 'SelectTopFilter':
      return {
        name: 'SelectTopFilter',
        displayName: t('Select Top'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'TopNCount',

        transformDataToDataRequestFunctions: (i: any) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '5',
            label: t('5'),
          },
          {
            value: '10',
            label: t('10'),
            isDefault: true,
          },
          {
            value: '15',
            label: t('15'),
          },
          {
            value: '20',
            label: t('20'),
          },
        ],
      };

    case 'ShowHeader':
      return {
        name: 'ShowHeader',
        displayName: t('Show Header'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInFileRequestBody: 'ShowHeader',
        transformDataToDataRequestFunctions: [(i: any) => i],
      };

    case 'ShowInvoicesWithFilter':
      return {
        name: 'ShowInvoicesWithFilter',
        displayName: t('Show Invoices'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'OnlyOutstandingInvoices',

        transformDataToDataRequestFunctions: [(i) => i === 'true'],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          { value: 'false', label: t('All invoices') },
          { value: 'true', label: t('With outstanding balances'), isDefault: true },
        ],
      };

    case 'StaffActingFilter':
      return {
        name: 'StaffActingFilter',
        displayName: t('Staff Acting'),
        component: StaffListFilter,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'StaffActingIds',

        transformDataToDataRequestFunctions: (i) => i,

        groupConfig: {
          groupName: 'Staff',
        },
      };
    case 'StaffAssistingFilter':
      return {
        name: 'StaffAssistingFilter',
        displayName: t('Staff Assisting'),
        component: StaffListFilter,
        componentProps: {
          staffType: 'StaffAssisting',
        },
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'StaffAssistingIds',

        transformDataToDataRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Staff',
        },
      };

    case 'StaffListMultiSelectFilter':
      return {
        name: 'StaffListMultiSelectFilter',
        component: StaffListFilter,
        validators: ['atLeastOneSelection'],
        displayName: t('Staff'),
        filterNamesInDataRequestBody: 'StaffIds',

        transformDataToDataRequestFunctions: (i) => i,
        config: { useIncInactive: false },
        groupConfig: {
          groupName: 'Staff',
        },
      };
    case 'StaffListSingleSelectFilter':
      return {
        name: 'StaffListSingleSelectFilter',
        displayName: t('Staff'),
        component: StaffListFilter,
        componentProps: {
          multiple: false,
        },
        validators: ['required'],
        filterNamesInDataRequestBody: 'StaffId',

        transformDataToDataRequestFunctions: (i) => i?.value,

        dropDownOptionTransform: (value: IStaffWithActiveFlag) => {
          return {
            label: value.Name,
            value: {
              label: value.Name,
              value: value.StaffId,
            },
            isActive: value?.IsActive,
          };
        },
        groupConfig: {
          groupName: 'Staff',
        },
      };

    case 'StaffListIdsSingleSelectFilter':
      return {
        name: 'StaffListIdsSingleSelectFilter',
        displayName: t('Staff'),
        component: StaffListFilter,
        validators: ['required'],
        componentProps: {
          multiple: false,
        },
        filterNamesInDataRequestBody: 'StaffId',

        transformDataToDataRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Staff',
        },
      };
    case 'StaffForAttributedWorkTo':
      return {
        name: 'StaffForAttributedWorkTo',
        component: StaffForAttributedWorkTo,
        validators: ['atLeastOneSelection'],
        displayName: t('Staff'),
        filterNamesInDataRequestBody: 'StaffIds',

        transformDataToDataRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Attribute Work To',
        },
      };

    case 'StaffResponsibleFilter':
      return {
        name: 'StaffResponsibleFilter',
        displayName: t('Staff Responsible'),
        component: StaffListFilter,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'StaffResponsibleIds',
        transformDataToDataRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Staff',
        },
      };

    case 'StaffTypeFilter': {
      return {
        name: 'StaffTypeFilter',
        displayName: t('Staff Type'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'StaffType',
        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        groupConfig: {
          groupName: 'Staff',
        },
        staticDropDownOptionsList: getStaffTypeList(),
      };
    }
    case 'FeeEarnerStaffTypeFilter': {
      return {
        name: 'FeeEarnerStaffTypeFilter',
        displayName: t('Staff Type'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'StaffType',
        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        groupConfig: {
          groupName: 'Staff',
        },
        staticDropDownOptionsList: getFeeEarnerSummaryStaffTypeList(),
      };
    }
    case 'AttributeDebtorsFilter': {
      return {
        name: 'AttributeDebtorsFilter',
        component: SingleSelect,
        validators: ['required'],
        displayName: t('Attribute Debtors'),
        filterNamesInDataRequestBody: 'attributeDebtors',
        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: getFeeEarnerSummaryAttributeList(),
      };
    }
    case 'StaffCreditFilter':
      return {
        name: 'StaffCreditFilter',
        displayName: t('Staff Credit'),
        component: StaffListFilter,
        componentProps: {
          staffType: 'StaffCredit',
        },
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'StaffCreditIds',
        transformDataToDataRequestFunctions: (i) => i,
        groupConfig: {
          groupName: 'Staff',
        },
      };
    case 'StartDateFilter':
      return {
        name: 'StartDateFilter',
        displayName: t('Start Date'),
        component: DatePicker,
        validators: ['validDate'],
        filterNamesInDataRequestBody: 'StartDate',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
      };
    case 'StatementDateFilter':
      return {
        name: 'StatementDateFilter',
        displayName: t('Statement Date'),
        component: React.lazy(() => import('./StatementDateFilter')),
        validators: [createRequireValidator('No statements are available')],
        filterNamesInDataRequestBody: ['StartDate', 'BankRecHistoryId'],
        noOptionsPlaceholder: t('No Statements'),
        transformDataToDataRequestFunctions: (
          i: {
            BankRecHistoryId: string;
            StatementDateString: string;
          },
          { filterName },
        ) => {
          if (filterName === 'StartDate') {
            return i?.StatementDateString;
          }
          return i?.BankRecHistoryId;
        },

        dropDownOptionTransform: (value: ITrustReconciliationDate) => {
          return {
            label: value?.StatementDateString,
            value: value,
          };
        },
      };

    case 'TimeFrameFilter': {
      return {
        name: 'TimeFrameFilter',
        displayName: t('Time Frame'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'TimeFrame',
        transformDataToDataRequestFunctions: (i) => i.toString(),
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'Cal',
            label: t('Calendar Year'),
            isDefault: true,
          },
          {
            value: 'Fin',
            label: t('Financial Year'),
          },
        ],
      };
    }
    case 'ToDateFilter':
      return {
        name: 'ToDateFilter',
        displayName: t('End Date'),
        component: DatePicker,
        validators: ['validDate'],
        filterNamesInDataRequestBody: 'To',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
      };

    case 'TransactionTypeFilter':
      return {
        name: 'TransactionTypeFilter',
        displayName: t('Type'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'TransactionTypes',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '1',
            label: t('Anticipated Payments'),
          },
          { value: '3', label: t('Cost Recoveries') },
          { value: '2', label: t('Journals') },
          { value: '0', label: t('Payments') },
          { value: '4', label: t('Time & Fees') },
        ],
      };

    case 'RegionFilter':
      return {
        name: 'RegionFilter',
        displayName: t('State'),
        component: React.lazy(() => import('./RegionFilter')),
        validators: ['required'],
        filterNamesInDataRequestBody: 'AddressState',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
      };
    case 'ReceiptsAtDateFilter':
      return {
        name: 'ReceiptsAtDateFilter',
        displayName: t('Receipts At'),
        component: DatePicker,
        validators: ['validDate'],
        filterNamesInDataRequestBody: 'receiptsAtDate',
        transformDataToDataRequestFunctions: (i) => i,
      };
    case 'SummaryItems': {
      return {
        name: 'SummaryItems',
        displayName: t('Summary Items'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInFileRequestBody: 'Sections',

        transformDataToDataRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'BilledToDate',
            label: t('Billed to Date'),
            isDefault: true,
          },
          {
            value: 'AgedUnbilled',
            label: t('Aged Unbilled'),
          },
          {
            value: 'Fees',
            label: t('Fees Unbilled & Billed'),
          },
          {
            value: 'TrustBalances',
            label: t('Trust Balances'),
          },
          {
            value: 'AgedDebtors',
            label: t('Aged Debtors'),
          },
        ],
      };
    }
    case 'SupplierOnly':
      return {
        name: 'SupplierOnly',
        displayName: t('Supplier Only'),
        component: CheckBox,
        validators: ['required'],
        filterNamesInDataRequestBody: 'IsSupplierOnly',
        transformDataToDataRequestFunctions: (i) => i,
      };

    case 'SupplierPaid':
      return {
        name: 'SupplierPaid',
        displayName: t('Supplier Paid'),
        component: MultiSelect,
        validators: ['atLeastOneSelection'],
        filterNamesInDataRequestBody: 'SupplierPaid',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '0',
            label: t('Payable'),
          },
          {
            value: '1',
            label: t('Not Payable'),
          },
        ],
      };

    case 'TrustAccountFilter':
      return {
        name: 'TrustAccountFilter',
        displayName: t('Bank Account'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'BankAccountId',

        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
        fetchFilterDataAPI: `${environment.reportingEndpoint}/api/filter/trustbankaccounts`,
        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: ITrustBankAccount) => {
          return {
            label: value?.BankAccountDesc,
            value: value?.BankAccId,
          };
        },
      };

    case 'TrustStatementActivity': {
      return {
        name: 'TrustStatementActivity',
        displayName: t('Matters'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'Activity',

        filterNamesInFileRequestBody: ['Activity', 'includeTransactionsWithinPeriod'],
        transformDataToDataRequestFunctions: [
          (i: string, { filterName } = {}) => {
            if (filterName === 'includeTransactionsWithinPeriod') {
              return i === '1';
            }
            return i?.toString();
          },
          (i) => i,
        ],
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: '0',
            label: t('With balances only'),
            isDefault: true,
          },
          {
            value: '1',
            label: t('With balances and/or transactions'),
          },
        ],
      };
    }
    case 'TrustTrialActivity': {
      return {
        name: 'TrustTrialActivity',
        displayName: t('Clients With'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'IncludeNilBalances',
        transformDataToDataRequestFunctions: (i) => {
          return i === 'true';
        },
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'false',
            label: t('With balances only'),
            isDefault: true,
          },
          {
            value: 'true',
            label: t('With balances and/or transactions'),
          },
        ],
      };
    }
    case 'TrustTrialOrderBy': {
      return {
        name: 'TrustTrialOrderBy',
        displayName: t('Order By'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInFileRequestBody: 'OrderBy',
        transformDataToFileRequestFunctions: (i) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'MatterNumber',
            label: t('Matter No.'),
            isDefault: true,
          },
          {
            value: 'ClientLastName',
            label: t('Client Last Name'),
          },
        ],
      };
    }

    case 'TransactionsFromFilter':
      return {
        name: 'TransactionsFromFilter',
        displayName: t('Transactions From'),
        component: DatePicker,
        validators: ['validDate'],
        defaultValue: () => {
          return startOfMonth();
        },
        filterNamesInDataRequestBody: 'StartDate',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
      };

    case 'UndertakingGivenFilter': {
      return {
        name: 'UndertakingGivenFilter',
        displayName: t('Undertaking Given'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'UndertakingGiven',

        transformDataToDataRequestFunctions: (i: any) => i,
        config: {
          isStatic: true,
        },
        staticDropDownOptionsList: [
          {
            value: 'All',
            label: t('All'),
            isDefault: true,
          },
          {
            value: 'Yes',
            label: t('Yes'),
          },
          {
            value: 'No',
            label: t('No'),
          },
        ],
      };
    }

    case 'UndertakingGivenAtDate':
      return {
        name: 'UndertakingGivenAtDate',
        displayName: t('Undertaking Given At Date'),
        component: DatePicker,
        validators: ['skip'],
        filterNamesInDataRequestBody: 'UndertakingGivenDate',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
      };

    case 'UndertakingDischargeDate':
      return {
        name: 'UndertakingDischargeDate',
        displayName: t('Undertaking Discharged Date'),
        component: DatePicker,
        validators: ['skip'],
        filterNamesInDataRequestBody: 'UndertakingDischargedDate',
        transformDataToDataRequestFunctions: [(i: any) => i.toString(), (i) => i],
      };
    case 'TrustGeneralYearFilter': {
      return {
        name: 'TrustGeneralYearFilter',
        displayName: t('Year'),
        component: SingleSelect,
        validators: ['required'],
        filterNamesInDataRequestBody: 'Year',

        transformDataToDataRequestFunctions: (i) => i,
        fetchFilterDataAPI: `${environment.reportingServiceEndpoint}/api/eom/filter/trustgeneral/years`,
        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: string, index, arr) => {
          if (arr.length === index + 1) {
            return {
              label: value,
              value: value,
            };
          }
          return {
            label: `${+value - 1} - ${+value}`,
            value: value,
          };
        },
      };
    }
    case 'TrustYearFilter': {
      return {
        name: 'TrustYearFilter',
        displayName: t('Year'),
        component: React.lazy(() => import('./TrustYearFilter')),
        filterNamesInDataRequestBody: 'Year',
        validators: [createRequireValidator('No year is available')],
        transformDataToDataRequestFunctions: (i) => i,
        config: { useIncInactive: false, isStatic: false },
        dropDownOptionTransform: (value: string) => {
          return {
            label: value,
            value: value,
          };
        },
      };
    }
    default:
      return {
        name: `not defined`,
        validators: ['skip'],
        component: null,
        displayName: '',
      };
  }
}

let currentModel: Record<string, IFilter> = {};
export function setCurrentFilterModel(filters: Filter[], extraOptions?: ConfigurableFilter[]) {
  // reset currentModel
  currentModel = {};

  filters.forEach((filter) => {
    const filterName = getFilterNameString(filter);
    const baseFilter = getBaseFilterModel(filterName);
    const defaultOptions = (filter as ConfigurableFilter)?.filterOptions;
    const options = extraOptions?.find((item) => item.filterName === filterName);
    currentModel[filterName] = {
      ...baseFilter,
      ...defaultOptions,
      ...options,
    };
  });
}
export function getCurrentFilterModel(filterName: FilterName): IFilter {
  if (currentModel[filterName]) {
    return currentModel[filterName];
  }
  currentModel[filterName] = getBaseFilterModel(filterName);
  return currentModel[filterName];
}

export function getAllFilter() {
  return Object.values(currentModel);
}

export function updateCurrentFilterModel(filter: Filter, payload: Record<string, any>) {
  const filterName = getFilterNameString(filter);
  const model = currentModel[filterName] || getBaseFilterModel(filterName);
  currentModel[filterName] = { ...model, ...payload };
}
